import { useState, useCallback, useEffect, useMemo } from "react";
import { EdgeZoneIndicator } from "@/three/components/EdgeZoneIndicator";
import { useGridInteraction } from "@/three/hooks/useGridInteraction";
import { useRealtimeCursors } from "@/three/hooks/useRealtimeCursors";
import { useRoadClustering } from "@/three/hooks/useRoadClustering";
import {
  VNScenarioOverlay,
  type VNScenario,
} from "@/components/Overray/VNScenarioOverlay";
import { useDeleteTenantMutation } from "@/hooks/useTenantQuery";
import { LoginPromptModal } from "@/components/LoginPromptModal";
import {
  BottomRightOverlay,
  TopRightOverlay,
  BottomLeftOverlay,
} from "@/components/Overray";
import { UserProfileCard } from "@/components/UserProfileCard";
import { useBuildingDetailStore } from "@/stores/buildingDetailStore";
import { useMousePositionStore } from "@/stores/mousePositionStore";
import { BackgroundCanvas, BuildingCanvas } from "@/three/components/Canvas";
import { useAuthStore } from "@/stores/authStore";
import type { CameraActions } from "@/three/events";
import { useSettingsStore } from "@/stores/settingsStore";
import { GRID_CONFIG } from "@/three/config/grid";
import { useUserProfile } from "@/three/hooks/useUserProfile";
import useSound from "use-sound";
import { useZipDownload } from "@/hooks/useZipDownload";
import { useGUITriangleAnimation } from "@/three/hooks/useGUITriangleAnimation";
import { useGUITriangleStore } from "@/stores/guiTriangleStore";
// 탭 컴포넌트 제거 - TopLeftOverlay로 통합됨
import { SystemMemoPad } from "@/components/SystemMemoPad";

/**
 * 메인 아이소메트릭 도시 그리드 페이지
 *
 * 인터랙션:
 * - 좌클릭: 건물 배치 / 선택
 * - 우클릭 드래그: 맵 패닝 (이동)
 * - 마우스 휠: 줌 인/아웃
 * - 가장자리 호버: 자동 스크롤 (조이스틱 방식, 설정에서 On/Off 가능)
 */
export function IsometricCityPage() {
  const { hasCompletedIntro, setIntroCompleted } = useAuthStore();

  // 전역 마우스 위치 상태
  const updateFromEvent = useMousePositionStore(
    (state) => state.updateFromEvent
  );
  const clearMousePosition = useMousePositionStore(
    (state) => state.clearMousePosition
  );

  // 마우스 이벤트 리스너 등록 (가장자리 스크롤 중에도 호버 감지)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      updateFromEvent(e);
    };

    const handleMouseLeave = () => {
      clearMousePosition();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [updateFromEvent, clearMousePosition]);

  // 가장자리 영역 상태 (UI 인디케이터용)
  const [edgeZone, setEdgeZone] = useState({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });

  // 지퍼 진행도 상태 (TopRight 오버레이 활성화 조건용)
  const zipperProgress = useBuildingDetailStore((s) => s.zipperProgress);
  const setZipperProgress = useBuildingDetailStore((s) => s.setZipperProgress);

  // 고정 설정값들 (다크모드=true, 건물숨기기=false)
  const isDarkMode = true;
  const hideBuildings = false;

  // settingsStore에서 필요한 설정만 가져오기
  const showOnlyMyBuildings = useSettingsStore(
    (state) => state.showOnlyMyBuildings
  );
  const filterMode = useSettingsStore((state) => state.filterMode);
  const filterUserId = useSettingsStore((state) => state.filterUserId);
  const setFilterMode = useSettingsStore((state) => state.setFilterMode);
  const enableEdgeScroll = useSettingsStore((state) => state.enableEdgeScroll);
  const setEnableEdgeScroll = useSettingsStore(
    (state) => state.setEnableEdgeScroll
  );

  // 게스트 로그인 유도 모달
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // 건물 에디터 상태
  const [editorModal, setEditorModal] = useState<{
    isOpen: boolean;
    x: number;
    z: number;
  }>({
    isOpen: false,
    x: 0,
    z: 0,
  });

  // 카메라 컨트롤 인스턴스 (씬 동기화용)
  const [cameraActions, setCameraActions] = useState<CameraActions | null>(
    null
  );

  // 에디터 박스 상태 (in-scene 에디터용)
  const [editorBoxes, setEditorBoxes] = useState<
    Array<{
      id: string;
      position: [number, number, number];
      scale: [number, number, number];
    }>
  >([]);
  const [selectedEditorBoxId, setSelectedEditorBoxId] = useState<string | null>(
    null
  );

  // 건물 상세 보기 상태 (In-Scene)
  const [selectedBuildingForDetail, setSelectedBuildingForDetail] = useState<
    string | null
  >(null);

  // 건물 선택이 변경되면 zipperProgress를 0.5로 리셋
  useEffect(() => {
    if (selectedBuildingForDetail) {
      setZipperProgress(0.5);
    }
  }, [selectedBuildingForDetail, setZipperProgress]);

  // 그리드 인터랙션
  const {
    placedObjects,
    buildings: allBuildings, // 필터링 전 모든 건물
    hoveredBuildingId, // 호버된 건물 ID
    onCellPointerOver,
    onCellPointerOut,
    onCellClick,
    refreshBuildings, // 건물 목록 새로고침
  } = useGridInteraction(showOnlyMyBuildings, {
    filterMode,
    filterUserId,
  });

  // 사용자 프로필 (컬러 설정)
  const { userColor } = useUserProfile();

  // 실시간 커서 (userId 전달)
  const { cursors, myCursor, updateCursor } = useRealtimeCursors(
    useAuthStore.getState().user?.id,
    userColor
  );

  // 도로 클러스터링용 원본 건물 목록 (필터링 전)
  // 내 건물만 보기 모드일 때는 원본 buildings를 사용하여 도로 계산
  const allBuildingsAsPlacedObjects = useMemo(() => {
    return allBuildings.map((b) => ({
      id: b.id,
      position: b.position,
      rotation: [0, 0, 0] as [number, number, number],
      scale: [1, 1, 1] as [number, number, number],
      meshIndex: b.meshIndex,
      buildingStructure: b.buildingStructure,
      buildingText: b.buildingText,
      title: b.title,
      userId: b.userId,
      userColor: b.userColor,
    }));
  }, [allBuildings]);

  // 카메라 메뉴 상태
  const [showCameraMenu, _setShowCameraMenu] = useState(false);
  const handleCameraReset = useCallback(() => {
    cameraActions?.resetCamera();
  }, [cameraActions]);

  // 도로 클러스터링 (캐싱 최적화)
  // 필터 모드와 관계없이 항상 모든 건물 기준으로 도로 생성 (도로는 전체 도시 인프라)
  const { roadSegments } = useRoadClustering(
    allBuildingsAsPlacedObjects,
    false // 항상 전체 건물 기준으로 도로 생성
  );

  // BGM 재생
  const [isPlaying, setIsPlaying] = useState(false); // 인트로 중에는 재생 안함
  const [playBGM, { stop: stopBGM }] = useSound(
    import.meta.env.BASE_URL + "/sound/city.mp3",
    {
      volume: 0.3,
      loop: true,
      interrupt: false,
    }
  );

  // 인트로 완료 후 BGM 재생
  const handleIntroComplete = useCallback(() => {
    setIntroCompleted(true);
    playBGM();
    setIsPlaying(true);
  }, [setIntroCompleted, playBGM]);

  // BGM 재생/중지 토글
  const toggleBGM = useCallback(() => {
    if (isPlaying) {
      stopBGM();
      setIsPlaying(false);
    } else {
      playBGM();
      setIsPlaying(true);
    }
  }, [isPlaying, playBGM, stopBGM]);

  // 건물 클릭 → In-Scene 상세 보기 열기
  const handleBuildingClick = useCallback(
    async (buildingId: string) => {
      // 에디터 모드일 때는 무시
      if (editorModal.isOpen) return;
      setSelectedBuildingForDetail(buildingId);
    },
    [editorModal.isOpen]
  );

  // 선택된 건물 데이터
  const selectedBuildingData = useMemo(() => {
    if (!selectedBuildingForDetail) return null;
    return allBuildings.find((b) => b.id === selectedBuildingForDetail) || null;
  }, [selectedBuildingForDetail, allBuildings]);

  // 입주 프리뷰 상태 (미리보기 텍스트)
  const [tenantPreview, setTenantPreview] = useState<{
    axis: "x" | "z";
    text: string;
    positionU: number;
    positionV: number;
  } | null>(null);

  // 비주얼 노벨 시나리오 상태 (Top-Left)
  const [vnScenario, setVnScenario] = useState<{
    type: VNScenario | "none";
    tenant?: any;
  }>({
    type: hasCompletedIntro ? "none" : "intro",
  });

  // 입주자 삭제 뮤테이션
  const { mutate: deleteTenant } = useDeleteTenantMutation(
    selectedBuildingForDetail
  );

  // 퇴거 확인 시나리오 시작
  const handleEvict = useCallback((tenant: any) => {
    setVnScenario({ type: "eviction", tenant });
  }, []);

  // VN 시나리오 완료 핸들러
  const handleVNComplete = useCallback(() => {
    if (vnScenario.type === "intro") {
      handleIntroComplete();
    } else if (vnScenario.type === "eviction" && vnScenario.tenant) {
      deleteTenant(vnScenario.tenant.id);
    }
    setVnScenario({ type: "none" });
  }, [vnScenario, handleIntroComplete, deleteTenant]);

  // VN 시나리오 취소 핸들러 (퇴거 거부 등)
  const handleVNCancel = useCallback(() => {
    setVnScenario({ type: "none" });
  }, []);

  // placedObjects에 프리뷰 테넌트 주입
  const placedObjectsWithPreview = useMemo(() => {
    if (!selectedBuildingForDetail || !tenantPreview || !tenantPreview.text) {
      return placedObjects;
    }
    return placedObjects.map((obj) => {
      if (obj.id === selectedBuildingForDetail) {
        const previewTenant = {
          id: "preview-tenant",
          buildingId: obj.id,
          userId: "preview",
          text: tenantPreview.text,
          projectionAxis: tenantPreview.axis as "x" | "-x" | "z" | "-z",
          positionU: tenantPreview.positionU,
          positionV: tenantPreview.positionV,
        };
        return {
          ...obj,
          tenants: [...(obj.tenants || []), previewTenant],
        };
      }
      return obj;
    });
  }, [placedObjects, selectedBuildingForDetail, tenantPreview]);

  // 선택된 건물의 크기 계산 (슬라이더 길이용)
  const selectedBuildingDimensions = useMemo(() => {
    if (!selectedBuildingData) return null;
    const structure = selectedBuildingData.buildingStructure || [];
    if (structure.length === 0) {
      const defaultSize = GRID_CONFIG.CELL_SIZE - 1;
      return { width: defaultSize, depth: defaultSize, height: 3 };
    }
    // 바운딩 박스 계산 (min/max 모두 계산하여 정확한 크기 구함)
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    let minZ = Infinity,
      maxZ = -Infinity;

    structure.forEach((box) => {
      // 박스의 최소/최대 좌표 계산
      const boxMinX = box.position[0] - box.scale[0] / 2;
      const boxMaxX = box.position[0] + box.scale[0] / 2;
      const boxMinY = box.position[1] - box.scale[1] / 2;
      const boxMaxY = box.position[1] + box.scale[1] / 2;
      const boxMinZ = box.position[2] - box.scale[2] / 2;
      const boxMaxZ = box.position[2] + box.scale[2] / 2;

      minX = Math.min(minX, boxMinX);
      maxX = Math.max(maxX, boxMaxX);
      minY = Math.min(minY, boxMinY);
      maxY = Math.max(maxY, boxMaxY);
      minZ = Math.min(minZ, boxMinZ);
      maxZ = Math.max(maxZ, boxMaxZ);
    });

    return {
      width: Math.max(maxX - minX, 1), // X축 전체 너비
      depth: Math.max(maxZ - minZ, 1), // Z축 전체 깊이
      height: Math.max(maxY - minY, 1), // Y축 전체 높이
    };
  }, [selectedBuildingData]);

  // 슬라이더 값 변경 핸들러
  const handleSliderUChange = useCallback(
    (u: number) => {
      if (tenantPreview) {
        setTenantPreview({ ...tenantPreview, positionU: u });
      }
    },
    [tenantPreview]
  );

  const handleSliderVChange = useCallback(
    (v: number) => {
      if (tenantPreview) {
        setTenantPreview({ ...tenantPreview, positionV: v });
      }
    },
    [tenantPreview]
  );

  // 빈 셀 클릭 → 에디터 모달 열기 (건물 상세 보기 중이면 무시)
  const handleEmptyCellClick = useCallback(
    (x: number, z: number) => {
      console.log("[handleEmptyCellClick] x:", x, "z:", z);

      // 건물 상세 보기 중이면 빈 셀 클릭 무시
      if (selectedBuildingForDetail) {
        console.log("[handleEmptyCellClick] Ignored - building detail open");
        return;
      }

      // 일반 모드: 건물 에디터 열기
      console.log("[handleEmptyCellClick] Opening building editor");
      setEditorModal({ isOpen: true, x, z });
    },
    [selectedBuildingForDetail]
  );

  // 에디터 모달 닫기
  const handleEditorClose = useCallback(() => {
    setEditorModal({ isOpen: false, x: 0, z: 0 });
  }, []);

  // ESC 키로 사이드바 닫기 + 카메라 줌 아웃 복원
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // 카메라 줌 아웃 복원
        if (cameraActions?.hasSavedState()) {
          await cameraActions.restoreState(0.5);
        }

        if (selectedBuildingForDetail) {
          setSelectedBuildingForDetail(null);
          setTenantPreview(null);
        } else if (editorModal.isOpen) {
          handleEditorClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedBuildingForDetail,
    editorModal.isOpen,
    handleEditorClose,
    setTenantPreview,
    cameraActions,
  ]);

  // 에디터 모드 또는 입주 모드일 때 가장자리 스크롤 비활성화 (설정값도 반영)
  useEffect(() => {
    if (!cameraActions) return;
    const isUIBlocked = editorModal.isOpen || !!selectedBuildingForDetail;
    const shouldEnable = enableEdgeScroll && !isUIBlocked;
    cameraActions.setEdgeScrollEnabled(shouldEnable);
  }, [
    cameraActions,
    editorModal.isOpen,
    selectedBuildingForDetail,
    enableEdgeScroll,
  ]);

  // 건물 생성 완료 시 새로고침
  const handleBuildingCreated = useCallback(() => {
    console.log("건물 생성 완료 - 데이터 새로고침");
    refreshBuildings();
  }, [refreshBuildings]);

  // 입주 완료 시 새로고침
  const handleTenantCreated = useCallback(() => {
    console.log("입주 완료 - 데이터 새로고침");
    refreshBuildings();
  }, [refreshBuildings]);

  // ZIP 다운로드 훅
  const { downloadZip } = useZipDownload();

  // 건물 생성 조건: 박스가 하나 이상 있고 바닥에 닿아야 함
  const canCreateBuilding =
    editorBoxes.length > 0 &&
    editorBoxes.some((box) => Math.abs(box.position[1]) < 0.01);

  // GUI 삼각형 애니메이션 (스토어 기반)
  useGUITriangleAnimation();
  const setGUIEnabled = useGUITriangleStore((state) => state.setEnabled);

  // 지퍼가 닫히면(0.05 초과) VN 퇴거 시나리오도 자동 종료
  useEffect(() => {
    if (vnScenario.type === "eviction" && zipperProgress > 0.05) {
      setVnScenario({ type: "none" });
    }
  }, [zipperProgress, vnScenario.type]);

  // GUI 삼각형 enabled 상태 업데이트
  useEffect(() => {
    // 건물 상세 보기 중이면서 지퍼가 일정 이상 열렸을 때 오른쪽 상단 활성화
    setGUIEnabled(
      "topRight",
      !!selectedBuildingForDetail && zipperProgress <= 0.05
    );

    setGUIEnabled(
      "bottomRight",
      !!selectedBuildingForDetail || editorModal.isOpen
    );
    setGUIEnabled("bottomLeft", editorModal.isOpen && canCreateBuilding);
  }, [
    selectedBuildingForDetail,
    zipperProgress,
    editorModal.isOpen,
    canCreateBuilding,
    vnScenario.type,
    setGUIEnabled,
  ]);

  // 지퍼 슬라이더가 1에 도달했을 때 ZIP 다운로드
  const handleZipComplete = useCallback(async () => {
    if (!selectedBuildingData) return;

    console.log("지퍼 슬라이더 완료 - ZIP 다운로드 시작");
    await downloadZip({
      buildingId: selectedBuildingData.id,
      buildingTitle: selectedBuildingData.title,
      buildingStructure: selectedBuildingData.buildingStructure,
      userColor: selectedBuildingData.userColor,
      tenants: selectedBuildingData.tenants || [],
    });
  }, [selectedBuildingData, downloadZip]);

  // ESC 동작을 버튼에서 호출할 핸들러
  const handleEscape = useCallback(async () => {
    // 카메라 줌 아웃 복원
    if (cameraActions?.hasSavedState()) {
      await cameraActions.restoreState(0.5);
    }

    if (selectedBuildingForDetail) {
      setSelectedBuildingForDetail(null);
      setTenantPreview(null);
    } else if (editorModal.isOpen) {
      handleEditorClose();
    }
  }, [
    cameraActions,
    selectedBuildingForDetail,
    editorModal.isOpen,
    handleEditorClose,
  ]);

  return (
    <div className="w-screen h-screen">
      {/* 비주얼 노벨 시나리오 오버레이 (인트로 가이드 / 퇴거 확인) */}
      <VNScenarioOverlay
        scenario={vnScenario.type as VNScenario}
        tenantData={vnScenario.tenant}
        onComplete={handleVNComplete}
        onCancel={handleVNCancel}
      />

      {/* 배경 캔버스 (z-index: 0) - InfiniteBackground, 도로, 커서, 마우스 이벤트 */}
      <BackgroundCanvas
        cursors={cursors}
        myCursor={myCursor}
        isDarkMode={isDarkMode}
        roadSegments={roadSegments}
        editorMode={editorModal.isOpen}
        selectedBuildingId={selectedBuildingForDetail}
        tenantSliderMode={!!(tenantPreview?.text && selectedBuildingDimensions)}
        // 마우스 이벤트 콜백
        onCellPointerOver={onCellPointerOver}
        onCellPointerOut={onCellPointerOut}
        onCellClick={
          // 에디터 모드일 때는 클릭 무시
          editorModal.isOpen
            ? () => {}
            : // 건물 클릭은 게스트도 허용, 빈 셀 클릭만 로그인 유도
              () =>
                onCellClick(handleBuildingClick, (x, z) => {
                  console.log("[onCellClick] Empty cell clicked at:", x, z);

                  if (useAuthStore.getState().isAnonymous()) {
                    setShowLoginPrompt(true);
                  } else {
                    handleEmptyCellClick(x, z);
                  }
                })
        }
        onBuildingNavigate={handleBuildingClick}
        updateCursor={updateCursor}
        onEdgeZoneChange={setEdgeZone}
        onControlsReady={(controls) => {
          setCameraActions(controls);
        }}
        isInputBlocked={false}
        cursorNone={!(editorModal.isOpen || !!selectedBuildingForDetail)}
      />

      {/* 건물 캔버스 (z-index: 200) - 건물, GUI 클리핑 */}
      <BuildingCanvas
        placedObjects={placedObjectsWithPreview}
        myCursor={myCursor}
        hoveredBuildingId={hoveredBuildingId}
        hideBuildings={hideBuildings}
        // 에디터 모드 props
        editorMode={editorModal.isOpen}
        editorPosition={{ x: editorModal.x, z: editorModal.z }}
        editorBoxes={editorBoxes}
        selectedEditorBoxId={selectedEditorBoxId}
        // 입주 슬라이더 props
        tenantSliderMode={!!(tenantPreview?.text && selectedBuildingDimensions)}
        tenantSliderBuilding={
          selectedBuildingData && selectedBuildingDimensions
            ? {
                position: [
                  selectedBuildingData.position[0],
                  0,
                  selectedBuildingData.position[2],
                ] as [number, number, number],
                width: selectedBuildingDimensions.width,
                depth: selectedBuildingDimensions.depth,
                height: selectedBuildingDimensions.height,
              }
            : null
        }
        tenantAxis={tenantPreview?.axis ?? "z"}
        tenantTextLength={tenantPreview?.text?.length ?? 0}
        tenantPreviewU={tenantPreview?.positionU ?? 0.5}
        tenantPreviewV={tenantPreview?.positionV ?? 0.5}
        onTenantPreviewUChange={handleSliderUChange}
        onTenantPreviewVChange={handleSliderVChange}
        selectedBuildingId={selectedBuildingForDetail}
        cursorNone={!(editorModal.isOpen || !!selectedBuildingForDetail)}
      />

      {/* BottomRight 오버레이 - 건물 선택 시 또는 빈 셀 에디터 (프리셋 + 박스 목록) */}
      <BottomRightOverlay
        mode={selectedBuildingForDetail ? "building" : "editor"}
        onZipComplete={handleZipComplete}
        onEscape={handleEscape}
        buildingProps={
          selectedBuildingData
            ? {
                buildingId: selectedBuildingData.id,
                buildingStructure: selectedBuildingData.buildingStructure || [],
                onTenantCreated: handleTenantCreated,
              }
            : undefined
        }
        editorProps={
          editorModal.isOpen
            ? {
                boxes: editorBoxes,
                setBoxes: setEditorBoxes,
                selectedBoxId: selectedEditorBoxId,
                setSelectedBoxId: setSelectedEditorBoxId,
                onClose: handleEditorClose,
              }
            : undefined
        }
      />

      {/* BottomLeft 오버레이 - 건물 정보 입력 + 액션 버튼 (박스가 있고 바닥에 닿을 때만) */}
      <BottomLeftOverlay
        editorProps={
          editorModal.isOpen
            ? {
                gridX: editorModal.x,
                gridZ: editorModal.z,
                boxes: editorBoxes,
                onClose: handleEditorClose,
                onBuildingCreated: handleBuildingCreated,
              }
            : undefined
        }
      />

      {/* TopRight 오버레이 - 지퍼 progress가 0.3 미만일 때 활성화 */}
      <TopRightOverlay
        buildingId={selectedBuildingForDetail}
        buildingData={
          selectedBuildingData
            ? {
                title: selectedBuildingData.title,
                description: selectedBuildingData.buildingText,
                ownerName: selectedBuildingData.username,
              }
            : undefined
        }
        onEvict={handleEvict}
      />

      {/* UI 오버레이 (마우스 이벤트 차단하지 않음) */}
      {hasCompletedIntro && (
        <>
          {/* 가장자리 영역 인디케이터 */}
          <EdgeZoneIndicator edgeZone={edgeZone} />

          {/* 통합 폴더 탭 UI (명함 & 메모지) */}
          <div
            style={{
              position: "fixed",
              top: "16px",
              left: "16px",
              zIndex: 400, // 건물에 가려지지 않게 높은 zIndex 설정
              pointerEvents: "none", // 내부 요소만 클릭 가능하게
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <UserProfileCard
              userColor={userColor}
              onLogout={() => {
                useAuthStore.getState().signOut();
                useAuthStore.getState().setIntroCompleted(false);
              }}
              onShowLoginPrompt={() => setShowLoginPrompt(true)}
              filterMode={filterMode}
              onFilterChange={(mode, userId) => setFilterMode(mode, userId)}
              isHidden={vnScenario.type !== "none"}
            />
            <SystemMemoPad
              isPlaying={isPlaying}
              toggleBGM={toggleBGM}
              onResetCamera={handleCameraReset}
              enableEdgeScroll={enableEdgeScroll}
              setEnableEdgeScroll={setEnableEdgeScroll}
              isHidden={vnScenario.type !== "none"}
            />
          </div>
        </>
      )}

      {/* 게스트 로그인 유도 모달 - hasCompletedIntro와 상관없이 항상 렌더링 */}
      <LoginPromptModal
        show={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        onStartIntro={() => {
          useAuthStore.getState().setIntroCompleted(false);
          setVnScenario({ type: "intro" });
        }}
      />
    </div>
  );
}
